/**
 * Product Personalization Logic
 * Handles visibility, validation, and submission of custom personalization fields.
 */
(function () {
    console.log('Personalization script loaded');

    const selectors = {
        wrapper: '[data-personalization-wrapper]',
        input: '[data-personalization-input]',
        error: '[data-personalization-error]',
        form: 'product-form form, form[action*="/cart/add"]',
        addBtn: 'button[name="add"], [type="submit"]',
        dynBtn: '[name="checkout"], .shopify-payment-button__button, .shopify-payment-button button',
        variantContainer: '[id^="variant-selects-"]',
        radio: 'input[type="radio"]',
        radioChecked: 'input[type="radio"]:checked',
        fieldset: 'fieldset'
    };

    class PersonalizationField {
        constructor(wrapper) {
            this.wrapper = wrapper;
            this.input = wrapper.querySelector(selectors.input);
            this.error = wrapper.querySelector(selectors.error);
            this.form = this.getForm();

            if (!this.input) {
                console.error('Personalization input not found in wrapper');
                return;
            }
            if (!this.form) {
                console.error('Personalization form not found');
                return;
            }

            this.triggerLabel = (this.wrapper.dataset.triggerLabel || '').toLowerCase();
            this.visibilityMode = this.wrapper.dataset.visibilityMode || 'variant_trigger';
            this.maxChars = parseInt(this.input.getAttribute('maxlength'), 10) || 60;
            this.maxLines = parseInt(this.wrapper.dataset.maxLines, 10) || 0;
            this.required = this.wrapper.dataset.required === 'true';
            this.noSelectionMessage = this.wrapper.dataset.noSelectionMessage || '';
            this.propertyName = this.input.getAttribute('name') || 'properties[Personalization]';

            console.log('Personalization initialized', {
                trigger: this.triggerLabel,
                mode: this.visibilityMode,
                maxChars: this.maxChars,
                maxLines: this.maxLines,
                property: this.propertyName
            });

            // Clean up name attribute on visible input so it doesn't submit directly
            this.input.removeAttribute('name');

            this.hiddenInput = this.getOrCreateHiddenInput();

            this.bindEvents();
            this.toggle(); // Initial check
        }

        getForm() {
            // 1. Try to find the product form by section ID
            // The wrapper is usually inside a section which has an ID like "MainProduct-template--..."
            // The form usually has an ID like "product-form-template--..."
            const section = this.wrapper.closest('[data-section]');
            if (section) {
                const sectionId = section.getAttribute('data-section');
                const form = document.getElementById(`product-form-${sectionId}`);
                if (form) return form;
            }

            // 2. Fallback: Try to find the closest form (though this might be the installment form)
            let form = this.wrapper.closest('form');
            if (form) return form;

            // 3. Fallback: specific selector for the main product form
            form = document.querySelector('product-form form[action*="/cart/add"]');
            if (form) return form;

            // 4. Last resort: look for any form that has an ID starting with product-form
            return document.querySelector('[id^="product-form"]');
        }

        getOrCreateHiddenInput() {
            let hidden = this.form.querySelector(`input[type="hidden"][name="${this.propertyName}"]`);
            if (!hidden) {
                hidden = document.createElement('input');
                hidden.type = 'hidden';
                hidden.name = this.propertyName;
                this.form.appendChild(hidden);
            }
            return hidden;
        }

        personalizationSelected() {
            // If mode is "always_visible", we always show the field regardless of variant
            if (this.visibilityMode === 'always_visible') {
                return true;
            }

            let container = this.form;
            const formId = this.form.getAttribute('id');

            // Try to find the variant picker container associated with this form
            if (formId && formId.startsWith('product-form-')) {
                const sectionId = formId.replace('product-form-', '');
                const variantContainer = document.getElementById('variant-selects-' + sectionId);
                if (variantContainer) {
                    container = variantContainer;
                }
            }

            const sets = container.querySelectorAll(selectors.fieldset).length ? container.querySelectorAll(selectors.fieldset) : document.querySelectorAll(selectors.fieldset);
            for (let i = 0; i < sets.length; i++) {
                const fs = sets[i];
                // The legend check is removed as per instruction.
                // const legend = (fs.querySelector('legend') || {}).textContent || '';
                // if (!legend || legend.toLowerCase().indexOf('personalization') === -1) continue;

                const checked = fs.querySelector(selectors.radioChecked);
                if (!checked) continue;

                const val = (checked.value || '').trim().toLowerCase();
                const lab = fs.querySelector(`label[for="${checked.id}"]`);
                const labTxt = (lab && (lab.innerText || lab.textContent) || '').trim().toLowerCase();

                // Check if the selected value OR label contains the trigger text
                if (val.includes(this.triggerLabel) || labTxt.includes(this.triggerLabel)) {
                    console.log('Personalization trigger matched:', val, labTxt);
                    return true;
                }
            }
            return false;
        }

        setDisabled(isDisabled) {
            const addBtn = this.form.querySelector(selectors.addBtn);
            const dynBtn = this.form.querySelector(selectors.dynBtn);

            if (addBtn) {
                addBtn.disabled = !!isDisabled;
                addBtn.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
            }
            if (dynBtn) {
                dynBtn.disabled = !!isDisabled;
                dynBtn.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
                dynBtn.style.pointerEvents = isDisabled ? 'none' : '';
                dynBtn.style.opacity = isDisabled ? '0.6' : '';
            }
        }

        clearError() {
            if (this.error) this.error.style.display = 'none';
            if (this.input) this.input.removeAttribute('aria-invalid');
        }

        showError(msg) {
            if (this.error) {
                this.error.textContent = msg;
                this.error.style.display = 'block';
            }
            if (this.input) {
                this.input.setAttribute('aria-invalid', 'true');
                this.input.focus();
            }
        }

        toggle() {
            const isSelected = this.personalizationSelected();

            if (isSelected) {
                if (this.wrapper.style.display !== 'block') {
                    this.wrapper.style.display = 'block';
                    // Re-validate when showing
                    this.validate();
                }
                this.hiddenInput.disabled = false;
                this.updateHiddenInput();
            } else {
                if (this.wrapper.style.display !== 'none') {
                    this.wrapper.style.display = 'none';
                    this.clearError();
                    this.setDisabled(false);
                }
                this.hiddenInput.disabled = true;
                this.hiddenInput.value = '';
            }
        }

        validate(e) {
            if (this.wrapper.style.display === 'none') {
                this.clearError();
                this.setDisabled(false);
                return true;
            }

            const val = (this.input.value || '').trim();

            if (this.required && val.length === 0) {
                if (e) e.preventDefault();
                this.showError(this.wrapper.dataset.errorMessageEmpty || 'Please enter a message.');
                this.setDisabled(true);
                return false;
            }

            if (val.length > this.maxChars) {
                if (e) e.preventDefault();
                this.showError(this.wrapper.dataset.errorMessageTooLong || 'Message is too long.');
                this.setDisabled(true);
                return false;
            }

            if (this.maxLines > 0) {
                const lines = val.split('\n').length;
                if (lines > this.maxLines) {
                    if (e) e.preventDefault();
                    this.showError(`Please limit your message to ${this.maxLines} lines.`);
                    this.setDisabled(true);
                    return false;
                }
            }

            this.clearError();
            this.setDisabled(false);
            return true;
        }

        updateHiddenInput() {
            const val = this.input.value.trim();
            if (!this.required && val === '' && this.noSelectionMessage) {
                this.hiddenInput.value = this.noSelectionMessage;
            } else {
                this.hiddenInput.value = this.input.value;
            }
        }

        bindEvents() {
            this.input.addEventListener('input', () => {
                this.updateHiddenInput();
                this.validate();
            });

            // Strict enforcement for max lines
            if (this.maxLines > 0) {
                this.input.addEventListener('keypress', (e) => {
                    if (e.which === 13) { // Enter key
                        const val = this.input.value;
                        const lines = (val.match(/\n/g) || []).length + 1;
                        if (lines >= this.maxLines) {
                            e.preventDefault();
                        }
                    }
                });

                this.input.addEventListener('paste', (e) => {
                    const pastedText = (e.clipboardData || window.clipboardData).getData('text/plain');
                    if (pastedText) {
                        const val = this.input.value;
                        // Simple prediction: current value + pasted text
                        // This is a rough check; for perfect cursor insertion prediction it's more complex,
                        // but this covers the main case of preventing overflow.
                        // A better approach for paste is to check the resulting line count.
                        // Since we can't easily predict the exact resulting value without selection logic,
                        // we'll do a conservative check on the pasted text itself + current lines.

                        // Better approach: let the paste happen, then check in 'input' event? 
                        // User requested "Prevent 'Paste' if limit exceeded".
                        // Let's use the user's provided logic style:
                        const currentLines = (val.match(/\n/g) || []).length + 1;
                        const pastedLines = (pastedText.match(/\n/g) || []).length;

                        // If we are already at limit and pasting a newline, block.
                        // Or if pasting adds enough newlines to exceed.
                        // Note: this doesn't account for pasting *over* selected text which might reduce lines.
                        // But it's a safe guard.

                        if (currentLines + pastedLines > this.maxLines) {
                            e.preventDefault();
                        }
                    }
                });
            }

            document.body.addEventListener('change', (e) => {
                if (e.target && e.target.matches(selectors.radio)) {
                    setTimeout(() => this.toggle(), 0);
                }
            });

            document.body.addEventListener('click', (e) => {
                if (e.target && (e.target.matches(selectors.radio) || e.target.closest('label'))) {
                    setTimeout(() => this.toggle(), 0);
                }
            });

            this.form.addEventListener('submit', (e) => this.validate(e));

            const addBtn = this.form.querySelector(selectors.addBtn);
            if (addBtn) addBtn.addEventListener('click', (e) => this.validate(e));

            const dynBtn = this.form.querySelector(selectors.dynBtn);
            if (dynBtn) dynBtn.addEventListener('click', (e) => this.validate(e));

            document.addEventListener('shopify:section:load', () => this.toggle());
        }
    }

    function init() {
        const wrappers = document.querySelectorAll(selectors.wrapper);
        wrappers.forEach(wrapper => new PersonalizationField(wrapper));
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
